import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../state'

import * as components from './components'

@ngCore.Component({
  imports: [
    components.editRecordDialog.Component,
    ds.layout.widget.Component,
    ds.table.Component,
    ds.layout.page.child.header.Component,
    ds.layout.page.Component,
    ds.overlay.confirmation.Component,
    ds.tabs.floatingHorizontal.Component,
    ds.tabs.tab.Directive,
    ngCommon.NgFor,
    ngCommon.NgIf,
  ],
  selector: 'atn-dms-model-page',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.dms.entities.Collection} */
  private readonly _entitiesEc = ngCore.inject(state.dms.entities.Collection)

  /** @see {@link state.dms.snapshots.Collection} */
  private readonly _snapshotsEc = ngCore.inject(state.dms.snapshots.Collection)

  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link state.dms.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.dms.records.Collection)

  /** @see {@link state.dms.attributes.Collection} */
  public readonly attributesEc = ngCore.inject(state.dms.attributes.Collection)

  /** Canonical table input for the current model's attributes. */
  public readonly attributesTableConfig = {
    collection: this.attributesEc,
    view: {
      search: true,
      title: 'Attributes',
    },
  } satisfies ds.table.ec.Config.Input<state.dms.attributes.TypeParams>

  /** Signal representing the current entity */
  public modelSg = ngCore.computed(
    (entitiesEc = this._entitiesEc, snapshotsEc = this._snapshotsEc) => {
      const entity = entitiesEc.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal()
      const snapshot = snapshotsEc.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal()

      return entity ?? snapshot
    },
  )

  /**
   * @see {@link ents.core.dms.models.Model.isStandard }
   */
  public readonly modelIsStandardSg = ngCore.computed(
    (model = this.modelSg()) =>
      !!model && ents.core.dms.models.Model.isStandard(model),
  )

  /** Name of the current model */
  public readonly modelNameSg = ngCore.computed(
    (model = this.modelSg()) => model?.name,
  )

  /** Canonical table input for the current model's records. */
  public readonly recordsTableConfigSg = ngCore.computed(
    (modelName = this.modelNameSg()) =>
      ({
        collection: this.recordsEc,
        view: {
          creation: true,
          pagination: true,
          search: true,
          title: modelName,
        },
      }) satisfies ds.table.ec.Config.Input<state.dms.records.TypeParams>,
  )

  /** Id of the current model */
  public readonly modelIdSg = ngCore.computed(
    (model = this.modelSg()) => model?.id,
  )

  /** Level of the current model */
  public readonly entityLevelSg = ngCore.computed((entity = this.modelSg()) =>
    this.isEntity(entity) ? entity.level : undefined,
  )

  /** Description of the current model */
  public readonly modelDescriptionSg = ngCore.computed(
    (model = this.modelSg()) => model?.description,
  )

  /**
   * @see {@link PartialId }
   */
  public readonly partialIdSg = ngCore.computed((model = this.modelSg()) =>
    model ? ents.core.dms.models.Id.Parts.from(model)?.partialId : undefined,
  )

  /** Parent entity name */
  public readonly entityParentNameSg = ngCore.computed(
    (entity = this.modelSg()) =>
      this.isEntity(entity)
        ? this._schemaFeature.selectors.schema
            .asSignal()
            ?.tryGetParentEntityOf(entity)?.name
        : undefined,
  )

  /** Child entity name */
  public readonly entityChildNameSg = ngCore.computed(
    (entity = this.modelSg()) =>
      this.isEntity(entity)
        ? this._schemaFeature.selectors.schema
            .asSignal()
            ?.tryGetChildEntityOf(entity)?.name
        : undefined,
  )

  /** Dimension name of the entity */
  public readonly entityDimensionNameSg = ngCore.computed(
    (entity = this.modelSg()) =>
      this.isEntity(entity)
        ? this._schemaFeature.selectors.schema
            .asSignal()
            ?.tryGetDimension(entity)?.name
        : undefined,
  )

  /** Breadcrumbs displayed in page header */
  public readonly breadcrumbsSg = ngCore.computed(
    () =>
      [
        {
          name: 'Catalog',
          url: this.meFeature.contexts.selectors
            .getUrlForLink({
              goto: {
                navigationId: ents.extra.prebuilt.AppPageAlias.Catalog,
              },
            })
            .asSignal(),
        },
        // TODO: Check this out!
        { name: this.modelNameSg() ?? '' },
      ] satisfies readonly ds.layout.page.child.header.models.Breadcrumb[],
  )

  /**
   * Exposing isEntity typechecker to template
   */
  public readonly isEntity = ents.core.dms.models.Entity.shouldBeEntity

  /**
   * Delete cancel button click handler
   */
  public cancelDeletionClickHandler() {
    this.recordsEc.plugins.editingPlugin.feature.actions.discard
      .from('Confirmation.Footer.Buttons.Cancel.Clicked')
      .dispatch({ delete: true })
  }
  /**
   * Delete confirm button click handler
   */
  public confirmDeletionClickHandler() {
    this.recordsEc.plugins.editingPlugin.feature.actions.submit
      .from('Confirmation.Footer.Buttons.Confirm.Clicked')
      .dispatch({ delete: true })
  }
}
